// #region Imports
import { startCase } from "lodash";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { searchTextFieldProps, topToolbarFitsThePage } from "_shared/_views/tables/table-toolbar";
import AHBoolean from "_shared/booleans/boolean";
import Guid from "_shared/guids/guid";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { 
  useArchiveFeeScheduleTemplateMutation, 
  useDeleteFeeScheduleTemplateMutation, 
  useGetFeeScheduleTemplatesByIdsQuery,
  useGetFeeScheduleTemplatesQuery, 
  useUnarchiveFeeScheduleTemplateMutation 
} from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { 
  FeeScheduleTemplateOrderParameters 
} from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import {
  FeeScheduleTemplateLoadIssue,
  getFeeScheduleTemplateQueryByInterface,
  getFeeScheduleTemplatesByIds,
  getFeeScheduleTemplatesByQuery,
  getLastSuccessfulFeeScheduleTemplateQueryByInterface,
  isFeeScheduleTemplateLoadIssue,
  resetFeeScheduleTemplatesByQueryState,
  updateFeeScheduleTemplateInterfaceQuery,
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleRowActions from "work/fee-schedule-templates/_views/_row-actions/fee-schedule-row-actions";
import FeeScheduleInternalActionsToolbar from "work/fee-schedule-templates/_views/_toolbars/fee-schedule-internal-actions-toolbar";
import FeeScheduleToolbar from "work/fee-schedule-templates/_views/_toolbars/fee-schedule-toolbar";
import { PageTab } from "work/fee-schedule-templates/_views/pages/tabs";
import { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import {
  FeeScheduleLoadIssue,
  FeeScheduleListItemJSON,
  useGetFeeSchedulesByQueryQuery,
} from "work/fee-schedules/_api/fee-schedules-api";
import FeeSchedulesQuery from "work/fee-schedules/_api/queries/contracts/fee-schedules-query";
import FeeSchedulesFilterParameters from "work/fee-schedules/_api/queries/contracts/filtering/fee-schedules-filter-parameters";
import FeeSchedulesOrderParameters, {
  FeeSchedulesOrderField,
} from "work/fee-schedules/_api/queries/contracts/ordering/fee-schedules-order-parameters";
import {
  getFeeSchedulesByQuery,
  getFeeSchedulesLastSuccessfulResponseByInterface,
  getFeeSchedulesQueryByInterface,
  isFeeScheduleLoadIssue,
  updateFeeSchedulesInterfaceQuery,
} from "work/fee-schedules/_store/fee-schedules-redux-slice";
import FeeSchedule from "work/fee-schedules/fee-schedule";
import BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import { useGetWorkAgreementsByIdsQuery } from "work/work-agreements/_api/work-agreement-api";
import { getWorkAgreementsByIds } from "work/work-agreements/_store/work-agreement-redux-slice";
import { startDraftProposalFromFeeScheduleTemplate } from "work/proposals/_views/drafts/start-draft-proposal-from-template";
import {
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
} from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
// #endregion

// #region Styled Components
const TagChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));
// #endregion

type ActiveFeeScheduleRow = (FeeSchedule | FeeScheduleLoadIssue) & {
  id: Guid;
  linkedFeeScheduleTemplates?: LinkedFeeScheduleTemplate[];
  workAgreement?: BarebonesWorkAgreementResponse;
};
type FeeSchedulePageRow = FeeScheduleTemplate | FeeScheduleTemplateLoadIssue | ActiveFeeScheduleRow;
type LinkedFeeScheduleTemplate = {
  id: Guid;
  template?: FeeScheduleTemplateJSON;
};

const columnHelper = createMRTColumnHelper<FeeSchedulePageRow>();
type DeserializedFeeScheduleTemplateRow = (FeeScheduleTemplate | FeeScheduleTemplateLoadIssue) & { id: Guid };

function deserializeFeeScheduleTemplates(
  feeScheduleTemplateJsonList: Array<FeeScheduleTemplateJSON | FeeScheduleTemplateLoadIssue>,
): DeserializedFeeScheduleTemplateRow[] {
  const deserializedData: DeserializedFeeScheduleTemplateRow[] = [];

  for (const feeScheduleTemplateJson of feeScheduleTemplateJsonList) {
    try {
      if (isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson)) {
        deserializedData.push(feeScheduleTemplateJson);
        continue;
      }

      const feeScheduleTemplate = FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
      if (!feeScheduleTemplate?.id) {
        console.error("FeeScheduleTemplate does not have an id", feeScheduleTemplateJson);
        deserializedData.push({ id: Guid.EMPTY, issue: "Error loading FeeScheduleTemplate." });
        continue;
      }

      deserializedData.push(feeScheduleTemplate as FeeScheduleTemplate & { id: Guid });
    } catch (error) {
      const id = "id" in feeScheduleTemplateJson ? (Guid.fromJSON(feeScheduleTemplateJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
      deserializedData.push({ id, issue: "Error loading FeeScheduleTemplate." });
      console.error("Error deserializing FeeScheduleTemplate JSON:", error);
    }
  }

  return deserializedData;
}

function deserializeActiveFeeSchedules(
  feeScheduleJsonList: Array<FeeScheduleListItemJSON | FeeScheduleLoadIssue>,
): ActiveFeeScheduleRow[] {
  const deserializedData: ActiveFeeScheduleRow[] = [];

  for (const feeScheduleJson of feeScheduleJsonList) {
    try {
      if (isFeeScheduleLoadIssue(feeScheduleJson)) {
        deserializedData.push({ ...feeScheduleJson, id: feeScheduleJson.id });
        continue;
      }

      const feeSchedule = FeeSchedule.fromJSON(feeScheduleJson) as FeeSchedule & { id: Guid };
      feeSchedule.id = feeSchedule.workAgreementId;
      deserializedData.push(feeSchedule);
    } catch (error) {
      const id = "workAgreementId" in feeScheduleJson
        ? (Guid.fromJSON(feeScheduleJson.workAgreementId) ?? Guid.EMPTY)
        : Guid.EMPTY;
      deserializedData.push({ id, issue: "Error loading Fee Schedule." });
      console.error("Error deserializing FeeSchedule JSON:", error);
    }
  }

  return deserializedData;
}

function applyActiveTabFilter(filterParameters: FeeSchedulesFilterParameters): FeeSchedulesFilterParameters {
  return new FeeSchedulesFilterParameters({
    ...filterParameters,
    isActive: new AHBoolean(true),
    isArchived: new AHBoolean(false),
  });
}

function applyTabFilter(
  filterParameters: FeeScheduleTemplateFilterParameters,
  activeTab: PageTab,
): FeeScheduleTemplateFilterParameters {
  switch (activeTab) {
    case PageTab.Active:
      return new FeeScheduleTemplateFilterParameters({
        ...filterParameters,
        isActive: new AHBoolean(true),
        isArchived: new AHBoolean(false),
      });
    case PageTab.Archived:
      return new FeeScheduleTemplateFilterParameters({
        ...filterParameters,
        isActive: undefined,
        isArchived: new AHBoolean(true),
      });
    case PageTab.Templates:
    default:
      return new FeeScheduleTemplateFilterParameters({
        ...filterParameters,
        isActive: new AHBoolean(false),
        isArchived: new AHBoolean(false),
      });
  }
}

function getFeeScheduleTemplateIdsForSession(feeSchedule: FeeSchedule, viewingAsVendor: boolean | undefined): Guid[] {
  return viewingAsVendor ? feeSchedule.vendorFeeScheduleTemplateIds : feeSchedule.clientFeeScheduleTemplateIds;
}

function getUniqueFeeScheduleTemplateIds(
  rows: ActiveFeeScheduleRow[] | undefined,
  viewingAsVendor: boolean | undefined,
): Guid[] {
  const idsByValue = new Map<string, Guid>();

  for (const row of rows ?? []) {
    if (isFeeScheduleLoadIssue(row)) continue;

    for (const id of getFeeScheduleTemplateIdsForSession(row, viewingAsVendor)) {
      idsByValue.set(id.value, id);
    }
  }

  return [...idsByValue.values()];
}

export default function FeeSchedulesPage() {
  // #region State
  const [globalFilter, setGlobalFilter] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });

  // #region Hooks
  const navigate = useNavigate();
  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();

  // #region Route
  const { pageTab: pageTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageTab = pageTabParam as PageTab | undefined;
  const effectivePageTab = pageTab ?? PageTab.Templates;

  // #region Permission Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();

  // #endregion

  // #region Fee Schedule Queries
  const interfaceName = `feeSchedule${startCase(effectivePageTab)}`;
  const isActiveTab = effectivePageTab === PageTab.Active;
  const feeScheduleTemplatesQueryParams = useSelector(getFeeScheduleTemplateQueryByInterface(interfaceName));
  const feeSchedulesQueryParams = useSelector(getFeeSchedulesQueryByInterface(interfaceName));

  const paginatedFeeScheduleTemplatesJson = useSelector(getFeeScheduleTemplatesByQuery(feeScheduleTemplatesQueryParams));
  const paginatedFeeScheduleTemplates = useMemo(() => {
    if (!paginatedFeeScheduleTemplatesJson) return;
    return deserializeFeeScheduleTemplates(paginatedFeeScheduleTemplatesJson.data);
  }, [paginatedFeeScheduleTemplatesJson]);

  const feeScheduleTemplatesAPIQuery = useGetFeeScheduleTemplatesQuery(
    !isActiveTab && feeScheduleTemplatesQueryParams ? { query: feeScheduleTemplatesQueryParams, interfaceName } : skipToken,
  );
  const paginatedFeeSchedulesJson = useSelector(getFeeSchedulesByQuery(feeSchedulesQueryParams));
  const paginatedFeeSchedules = useMemo(() => {
    if (!paginatedFeeSchedulesJson) return;
    return deserializeActiveFeeSchedules(paginatedFeeSchedulesJson.data);
  }, [paginatedFeeSchedulesJson]);

  const feeSchedulesAPIQuery = useGetFeeSchedulesByQueryQuery(
    isActiveTab && feeSchedulesQueryParams ? { query: feeSchedulesQueryParams, interfaceName } : skipToken,
  );
  const [archiveFeeScheduleTemplate] = useArchiveFeeScheduleTemplateMutation();
  const [unarchiveFeeScheduleTemplate] = useUnarchiveFeeScheduleTemplateMutation();
  const [deleteFeeScheduleTemplate] = useDeleteFeeScheduleTemplateMutation();

  const lastSuccessfulTemplateQueryResponseJson = useSelector(getLastSuccessfulFeeScheduleTemplateQueryByInterface(interfaceName));

  const lastSuccessfulTemplateQueryResponse = useMemo(() => {
    if (!lastSuccessfulTemplateQueryResponseJson) return;
    return deserializeFeeScheduleTemplates(lastSuccessfulTemplateQueryResponseJson.data);
  }, [lastSuccessfulTemplateQueryResponseJson]);

  const lastSuccessfulFeeSchedulesQueryResponseJson = useSelector(getFeeSchedulesLastSuccessfulResponseByInterface(interfaceName));

  const lastSuccessfulFeeSchedulesQueryResponse = useMemo(() => {
    if (!lastSuccessfulFeeSchedulesQueryResponseJson) return;
    return deserializeActiveFeeSchedules(lastSuccessfulFeeSchedulesQueryResponseJson.data);
  }, [lastSuccessfulFeeSchedulesQueryResponseJson]);

  const tableTemplates = paginatedFeeScheduleTemplates ?? lastSuccessfulTemplateQueryResponse;
  const tableFeeSchedules = paginatedFeeSchedules ?? lastSuccessfulFeeSchedulesQueryResponse;
  const activeWorkAgreementIds = useMemo(
    () =>
      (tableFeeSchedules ?? [])
        .filter((row): row is FeeSchedule & { id: Guid } => !isFeeScheduleLoadIssue(row))
        .map((feeSchedule) => feeSchedule.workAgreementId),
    [tableFeeSchedules],
  );

  const workAgreementsByIdsAPIQuery = useGetWorkAgreementsByIdsQuery(
    isActiveTab && activeWorkAgreementIds.length
      ? { workAgreementIds: activeWorkAgreementIds.map((id) => id.value) }
      : skipToken,
  );
  const barebonesWorkAgreements = useSelector(getWorkAgreementsByIds(activeWorkAgreementIds));
  const barebonesWorkAgreementsById = useMemo(() => {
    return new Map(barebonesWorkAgreements.map((workAgreement) => [workAgreement.id, workAgreement]));
  }, [barebonesWorkAgreements]);
  const viewingAsVendor = session.context?.viewingAsVendor;
  const activeFeeScheduleTemplateIds = useMemo(
    () => getUniqueFeeScheduleTemplateIds(tableFeeSchedules, viewingAsVendor),
    [tableFeeSchedules, viewingAsVendor],
  );
  const activeFeeScheduleTemplatesByIdsAPIQuery = useGetFeeScheduleTemplatesByIdsQuery(
    isActiveTab && activeFeeScheduleTemplateIds.length
      ? { feeScheduleTemplateIds: activeFeeScheduleTemplateIds.map((id) => id.value) }
      : skipToken,
  );
  const activeFeeScheduleTemplates = useSelector(getFeeScheduleTemplatesByIds(activeFeeScheduleTemplateIds));
  const activeFeeScheduleTemplatesById = useMemo(() => {
    return new Map(
      activeFeeScheduleTemplates
        .filter((template): template is FeeScheduleTemplateJSON & { id: string } => Boolean(template?.id))
        .map((template) => [template.id, template]),
    );
  }, [activeFeeScheduleTemplates]);
  const tableActiveFeeSchedules = useMemo<ActiveFeeScheduleRow[] | undefined>(() => {
    if (!tableFeeSchedules) return;
    return tableFeeSchedules.map((feeSchedule) => {
      if (isFeeScheduleLoadIssue(feeSchedule)) return feeSchedule;
      feeSchedule.workAgreement = barebonesWorkAgreementsById.get(feeSchedule.workAgreementId.value);
      feeSchedule.linkedFeeScheduleTemplates = getFeeScheduleTemplateIdsForSession(feeSchedule, viewingAsVendor)
        .map((templateId) => ({
          id: templateId,
          template: activeFeeScheduleTemplatesById.get(templateId.value),
        }));
      return feeSchedule;
    });
  }, [activeFeeScheduleTemplatesById, barebonesWorkAgreementsById, tableFeeSchedules, viewingAsVendor]);

  useEffect(() => {
    if (isActiveTab) {
      const filterParameters = applyActiveTabFilter(
        FeeSchedulesFilterParameters.fromSearchParams({ searchParams, session }),
      );
      const orderParameters = FeeSchedulesOrderParameters.fromSearchParams(searchParams);

      const newQuery = new FeeSchedulesQuery({
        filterParameters,
        orderParameters,
        paginationParameters: PaginationParameters.fromSearchParams(searchParams),
      });
      if (feeSchedulesQueryParams && newQuery.isEqualTo(feeSchedulesQueryParams)) return;
      dispatch(
        updateFeeSchedulesInterfaceQuery({
          interfaceName,
          query: newQuery,
        }),
      );
      return;
    }

    const filterParameters = applyTabFilter(
      FeeScheduleTemplateFilterParameters.fromSearchParams({ searchParams, session }),
      effectivePageTab,
    );
    const orderParameters = FeeScheduleTemplateOrderParameters.fromSearchParams(searchParams);

    const newQuery = new FeeScheduleTemplateQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (feeScheduleTemplatesQueryParams && newQuery.isEqualTo(feeScheduleTemplatesQueryParams)) return;
    dispatch(
      updateFeeScheduleTemplateInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [
    effectivePageTab,
    session.user?.legalEntityId.value,
    session,
    searchParams,
    feeScheduleTemplatesQueryParams,
    feeSchedulesQueryParams,
    dispatch,
    interfaceName,
    isActiveTab,
  ]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (globalFilter) {
      newSearchParams.set("query", globalFilter);
    }

    if (!isActiveTab) {
      const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
      if (nameFilter) {
        newSearchParams.set("name", nameFilter);
      }

      const tagsFilter = columnFilters.find((filter) => filter.id === "tags")?.value as string | undefined;
      if (tagsFilter) {
        newSearchParams.set("tags", tagsFilter);
      }
    }

    const orderFields = isActiveTab ? FeeSchedulesOrderField : FeeScheduleTemplateOrderField;
    const orderingField = sorting.find((sort) =>
      Object.values(orderFields)
        .map((field) => field.toLowerCase())
        .includes(sort.id.toLowerCase()),
    );

    if (orderingField) {
      newSearchParams.set("orderBy", orderingField.id);
      newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
    }

    const paginationPageIndex = pagination.pageIndex;
    if (paginationPageIndex !== undefined) newSearchParams.set("page", (paginationPageIndex + 1).toString());

    const paginationPageSize = pagination.pageSize;
    if (paginationPageSize !== undefined) newSearchParams.set("pageSize", paginationPageSize.toString());

    if (newSearchParams.toString() === searchParams.toString()) return;

    setSearchParams(newSearchParams);
  }, [globalFilter, columnFilters, sorting, pagination, searchParams, setSearchParams, isActiveTab]);

  useEffect(() => {
    if (feeScheduleTemplatesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedule templates table", { variant: "error" });
    }
  }, [feeScheduleTemplatesAPIQuery.isError]);

  useEffect(() => {
    if (feeSchedulesAPIQuery.isError) {
      enqueueSnackbar("Error loading active fee schedules table", { variant: "error" });
    }
  }, [feeSchedulesAPIQuery.isError]);

  // #region Functions
  /**
   * Archive or Unarchive the specified fee schedule template
   * @param id fee schedule id to archive / unarchive
   * @param archive if true, archive, else unarchive
   */
  const handleArchiveFeeScheduleTemplate = useCallback(async (id: Guid | undefined, archive: boolean): Promise<void> => {
    if (!id) {
      throw new Error("No fee schedule template id provided");
    }

    const action = archive ? "Archive" : "Unarchive";

    const response = await confirm({
      title: action,
      message: `${action} fee schedule template?`,
      okButtonText: action,
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      if (archive) await archiveFeeScheduleTemplate({ templateId: id.value }).unwrap();
      else await unarchiveFeeScheduleTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar(`${action} fee schedule`, { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Failed to ${action} fee schedule. Please try again`, {
        variant: "error",
      });
    }
  }, [confirm, archiveFeeScheduleTemplate, unarchiveFeeScheduleTemplate, dispatch]);

  /**
   * Deletes the specified fee schedule template
   * @param id The fee schedule template id
   */
  const handleDeleteFeeScheduleTemplate = useCallback(async (id: Guid | undefined): Promise<void> => {
    if (!id) {
      throw new Error("No fee schedule template id provided");
    }

    const response = await confirm({
      title: "Delete?",
      message: `Delete fee schedule template?`,
      okButtonText: "Delete",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      await deleteFeeScheduleTemplate({ templateId: id.value }).unwrap();
      enqueueSnackbar("Deleted fee schedule", { variant: "success" });
      dispatch(resetFeeScheduleTemplatesByQueryState());
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete fee schedule. Please try again", {
        variant: "error",
      });
    }
  }, [confirm, dispatch, deleteFeeScheduleTemplate]);

  const handleCreateProposalFromFeeScheduleTemplate = useCallback((template: FeeScheduleTemplate): void => {
    startDraftProposalFromFeeScheduleTemplate({
      dispatch,
      navigate,
      session,
      template,
    });
  }, [dispatch, navigate, session]);
  // #endregion

  const columns = useMemo(
    () => {
      if (isActiveTab) {
        return [
          columnHelper.display({
            header: "Work Agreement",
            id: "Name",
            Cell: ActiveWorkAgreementCell,
          }),
          columnHelper.display({
            header: "Description",
            id: "description",
            enableSorting: false,
            Cell: ActiveDescriptionCell,
          }),
          columnHelper.display({
            header: "Hire Date",
            id: "hireDate",
            enableSorting: false,
            Cell: ActiveHireDateCell,
          }),
          columnHelper.display({
            header: "Categories",
            id: "categories",
            enableSorting: false,
            Cell: ActiveCategoriesCell,
          }),
          columnHelper.display({
            header: "Template",
            id: "template",
            enableSorting: false,
            Cell: ActiveTemplateCell,
          }),
        ];
      }

      return [
        columnHelper.display({
          header: "Name",
          id: "name",
          Cell: ({ row, table }) => <NameCell row={row} table={table} effectivePageTab={effectivePageTab} />
        }),
        columnHelper.display({
          header: "Tags",
          id: "tags",
          enableSorting: false,
          filterVariant: "multi-select",
          Cell: TagsCell
        }),
        columnHelper.display({
          header: "Created",
          id: "createdDate",
          Cell: CreatedDateCell
        }),
      ];
    },
    [effectivePageTab, isActiveTab],
  );

  const openFeeSchedule = useCallback(
    (feeSchedule: FeeSchedulePageRow) => {
      if (isActiveTab) {
        if (isFeeScheduleLoadIssue(feeSchedule)) {
          console.warn("Cannot open fee schedule due to load issue:", feeSchedule.issue);
          return;
        }
        if (!("workAgreementId" in feeSchedule)) return;
        navigate(`/work/active/view/${feeSchedule.workAgreementId.value}/fee-schedule`);
        return;
      }

      if (isFeeScheduleTemplateLoadIssue(feeSchedule)) {
        console.warn("Cannot open fee schedule template due to load issue:", feeSchedule.issue);
        return;
      }
      const destination = feeScheduleTemplateDestination(feeSchedule, effectivePageTab);
      if (!destination) {
        console.error("Cannot open fee schedule template without id");
        return;
      }

      navigate(destination);
    },
    [isActiveTab, navigate, effectivePageTab]
  );

  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<FeeSchedulePageRow> }) => ({
      onClick: () => openFeeSchedule(row.original),
      sx: {
        backgroundColor: isFeeScheduleTemplateLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" : "inherit",
        cursor: isFeeScheduleTemplateLoadIssue(row.original) ? "default" : "pointer",
      },
    }),
    [openFeeSchedule]
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<FeeSchedulePageRow> }) => (
      <FeeScheduleInternalActionsToolbar table={props.table} />
    ), []
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FeeSchedulePageRow> }) => (
      <FeeScheduleToolbar table={table} activeTab={pageTab ?? PageTab.Templates} />
    ), [pageTab]
  );

  const renderRowActions = useCallback(
    (props: { row: MRT_Row<FeeSchedulePageRow> }) => {
      const { row } = props;
      if (isActiveTab) return null;
      if (isFeeScheduleTemplateLoadIssue(row.original)) return null;
      return (
        <FeeScheduleRowActions
          row={row as MRT_Row<FeeScheduleTemplate | FeeScheduleTemplateLoadIssue>}
          activeTab={effectivePageTab}
          onArchivedChange={handleArchiveFeeScheduleTemplate}
          onCreateProposal={handleCreateProposalFromFeeScheduleTemplate}
          onDelete={handleDeleteFeeScheduleTemplate}
        />
      );
    },
    [
      effectivePageTab,
      handleArchiveFeeScheduleTemplate,
      handleCreateProposalFromFeeScheduleTemplate,
      handleDeleteFeeScheduleTemplate,
      isActiveTab,
    ]
  );

  const getRowId = useCallback((row: FeeSchedulePageRow, index: number) => {
    if (isFeeScheduleTemplateLoadIssue(row)) return `template-load-issue-${row.id.value}-${index}`;
    if (isFeeScheduleLoadIssue(row)) return `fee-schedule-load-issue-${row.id.value}-${index}`;
    if ("workAgreementId" in row) return row.workAgreementId.value;
    return row.id?.value ?? "";
  }, []);

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (isActiveTab && feeSchedulesAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
    if (!isActiveTab && feeScheduleTemplatesAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
  }, [feeScheduleTemplatesAPIQuery.isError, feeSchedulesAPIQuery.isError, isActiveTab]);

  const tableRows = isActiveTab ? tableActiveFeeSchedules : tableTemplates;
  const tableIsFetching = isActiveTab
    ? feeSchedulesAPIQuery.isFetching || workAgreementsByIdsAPIQuery.isFetching || activeFeeScheduleTemplatesByIdsAPIQuery.isFetching
    : feeScheduleTemplatesAPIQuery.isFetching;
  const tableIsLoading = isActiveTab ? feeSchedulesAPIQuery.isLoading : feeScheduleTemplatesAPIQuery.isLoading;
  const tableIsError = isActiveTab ? feeSchedulesAPIQuery.isError : feeScheduleTemplatesAPIQuery.isError;

  const tableOptions = useMemo<MRT_TableOptions<FeeSchedulePageRow>>(() => {
    return {
      columns,
      data: tableRows ?? [],
      enableRowSelection: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      muiTableBodyCellProps: ({ cell }) => ({
        sx: (theme) => feeScheduleTableCellStyles(theme, {
          columnId: cell.column.id,
          truncate: cell.column.id !== "tags" && cell.column.id !== "template",
        }),
      }),
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      getRowId,
      enableRowActions: effectivePageTab !== PageTab.Active,
      enableColumnPinning: true,
      enableFacetedValues: true,
      initialState: {
        columnPinning: { right: ["mrt-row-actions"] },
        showGlobalFilter: true,
      },
      positionGlobalFilter: "none",
      muiSearchTextFieldProps: ({ table }) =>
        searchTextFieldProps({ searches: "fee schedules", table }),
      renderEmptyRowsFallback: emptyRowsFallback({
        isError: Boolean(tableIsError),
        isLoading: tableIsFetching,
        onRetry: () => void (isActiveTab ? feeSchedulesAPIQuery.refetch() : feeScheduleTemplatesAPIQuery.refetch()),
        // Each tab holds something different, and the tab's own name is not it.
        subject:
          effectivePageTab === PageTab.Templates
            ? "fee schedule templates"
            : effectivePageTab === PageTab.Archived
              ? "archived fee schedules"
              : "fee schedules",
      }),
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      renderRowActions,
      muiTopToolbarProps: { sx: topToolbarFitsThePage },
      muiTableContainerProps: {
        sx: {
          flex: 1,
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      },
      muiToolbarAlertBannerProps,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      rowCount: tableRows?.length ?? 0,
      state: {
        columnFilters,
        globalFilter,
        isLoading: tableIsFetching,
        pagination,
        showAlertBanner: Boolean(tableIsError),
        showProgressBars: tableIsFetching,
        sorting,
        showSkeletons: tableIsLoading,
      },
    };
  }, [
    columns, 
    tableRows, 
    muiTableBodyRowProps, 
    renderToolbarInternalActions, 
    renderTopToolbarCustomActions, 
    getRowId, 
    effectivePageTab, 
    renderRowActions, 
    muiToolbarAlertBannerProps, 
    columnFilters, 
    globalFilter, 
    tableIsFetching, 
    tableIsError, 
    tableIsLoading, 
    isActiveTab, 
    feeSchedulesAPIQuery, 
    feeScheduleTemplatesAPIQuery, 
    pagination, 
    sorting
  ]);

  // #region Render
  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToFeeSchedules) return <NotAuthorized />;

  return <MRTNoMemo<FeeSchedulePageRow> options={tableOptions} />;
  // #endregion
}

/**
 * Where a template row goes, in one place, because the row and the link in its name cell have to
 * agree — and the tab decides whether the row opens the editor or a read-only view.
 */
function feeScheduleTemplateDestination(row: FeeSchedulePageRow, effectivePageTab: PageTab): string | undefined {
  if (isFeeScheduleTemplateLoadIssue(row)) return undefined;
  if ("workAgreementId" in row) return undefined;
  if (!row.id) return undefined;
  const action = effectivePageTab === PageTab.Templates ? "edit" : "view";
  return `/fee-schedules/${effectivePageTab}/${action}/${row.id.value}`;
}

const NameCell = ({ row, table, effectivePageTab }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>,
  effectivePageTab: PageTab
}) => {
  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) {
    return (
      <Tooltip
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: "4000px",
              whiteSpace: "normal",
            },
          },
        }}
        title={`Our staff has been notified. Reference id: ${row.original.id.value}`}
      >
        <span>Error Loading Template</span>
      </Tooltip>
    );
  }
  if ("workAgreementId" in row.original) return;
  return (
    <FeeScheduleTableCellText emphasized>
      <RowLink to={feeScheduleTemplateDestination(row.original, effectivePageTab)}>{row.original.name}</RowLink>
    </FeeScheduleTableCellText>
  );
};

const TagsCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading) return;
  if (isFeeScheduleTemplateLoadIssue(row.original)) return;
  if ("workAgreementId" in row.original) return;
  const template = row.original as FeeScheduleTemplate;
  if (!template.tags.length) return <TagChip disabled label="No Tags" size="small" />;
  return (
    <div>
      {template.tags.map((tag) => (
        <TagChip key={tag.value as React.Key} label={tag.value} size="small" />
      ))}
    </div>
  );
};

const CreatedDateCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading) return;
  if ("workAgreementId" in row.original) return;
  if (isFeeScheduleTemplateLoadIssue(row.original) || !row.original.created) return;
  return <time>{row.original.created?.asFormattedString}</time>;
};

const ActiveWorkAgreementCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading) return;
  if (isFeeScheduleLoadIssue(row.original)) {
    return (
      <Tooltip title={`Our staff has been notified. Reference id: ${row.original.id.value}`}>
        <span>Error Loading Fee Schedule</span>
      </Tooltip>
    );
  }
  if (!("workAgreementId" in row.original)) return;
  const value = row.original.workAgreement?.name ?? row.original.workAgreementId.value;
  return <FeeScheduleTableCellText emphasized>{value}</FeeScheduleTableCellText>;
};

const ActiveDescriptionCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  const description = row.original.workAgreement?.description;
  return description ? <FeeScheduleTableCellText>{description}</FeeScheduleTableCellText> : <Icon>remove</Icon>;
};

const ActiveHireDateCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  return row.original.hireDate ? <time>{row.original.hireDate.asFormattedString}</time> : <Icon>remove</Icon>;
};

const ActiveCategoriesCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  return row.original.categories.length;
};

const ActiveTemplateCell = ({ row, table }: {
  row: { original: FeeSchedulePageRow },
  table: MRT_TableInstance<FeeSchedulePageRow>
}) => {
  if (table.getState().isLoading || isFeeScheduleLoadIssue(row.original) || !("workAgreementId" in row.original)) return;
  const linkedTemplates = row.original.linkedFeeScheduleTemplates ?? [];
  if (!linkedTemplates.length) return <Icon>remove</Icon>;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {linkedTemplates.map(({ id, template }) => {
        const templatePath = template?.archived
          ? `/fee-schedules/archived/view/${id.value}`
          : `/fee-schedules/templates/edit/${id.value}`;
        return (
          <Link
            key={id.value}
            component={RouterLink}
            to={templatePath}
            onClick={(event) => event.stopPropagation()}
          >
            {template?.name ?? id.value}
          </Link>
        );
      })}
    </Box>
  );
};

